/**
 * The ids of entities with an unsent write in the outbox — the tree's "pending" dot
 * (`060-notebook-tree.md` §6: a create that has not reached the server "stays in the tree,
 * rendered with a subtle pending dot. It is fully editable; the edits queue behind it.").
 *
 * The dot is not an error state. The page really does exist, durably, on this device.
 */
import { useEffect, useState } from 'react';
import { listEntries, subscribe } from '@/offline/outbox';

const EMPTY: ReadonlySet<string> = new Set();

function sameIds(a: ReadonlySet<string>, b: ReadonlySet<string>): boolean {
  return a.size === b.size && [...b].every((id) => a.has(id));
}

export function usePendingEntities(): ReadonlySet<string> {
  const [ids, setIds] = useState<ReadonlySet<string>>(EMPTY);

  useEffect(() => {
    let live = true;

    const read = (): void => {
      void listEntries().then((entries) => {
        if (!live) return;
        const next = new Set(entries.map((entry) => entry.entityId));
        // Referential stability: no re-render of the whole tree when nothing changed.
        setIds((previous) => (sameIds(previous, next) ? previous : next));
      });
    };

    read();
    const unsubscribe = subscribe(read);

    return () => {
      live = false;
      unsubscribe();
    };
  }, []);

  return ids;
}
